import React, {
  useState,
  useEffect,
  createRef,
  useRef,
  useCallback,
} from 'react';
import clsx from 'clsx';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Scrollbars } from 'react-custom-scrollbars';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import TablePagination from '@material-ui/core/TablePagination';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';

import { tableChangeLoading } from 'modules/filter';
import { showSuccessSnackbar } from 'modules/snackbar';
import { paramConverter } from 'modules/common';
import {
  fetchHeader,
  under2camel,
  fileDownload,
  nullConversion,
  converCalFlagData,
} from 'lib/commonLib';
import { tableHeaders, setCustom } from 'lib/api/common';

import CurrentStatus from 'components/atoms/CurrentStatus';
import DivisionWrap from 'components/atoms/DivisionWrap';
import SetUpList from 'components/molecules/SetUpList';
import EnhancedTableWrap from 'components/molecules/NewTable/EnhancedTableWrap';
import EnhancedTableHead from 'components/molecules/NewTable/EnhancedTableHead';
import EnhancedTableBody from 'components/molecules/NewTable/EnhancedTableBody';

const useStyles = makeStyles((theme) => ({
  dimming: {
    zIndex: theme.zIndex.table.dimming,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgb(255 255 255 / 0.6)',
  },
  fullscreen: {
    position: 'fixed',
    zIndex: theme.zIndex.fullScreen,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    padding: 20,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  tableWrap: { position: 'relative' },
  tableCustomScrollBar: {
    '& > div:first-child': { position: 'relative !important' },
    '&.isModalHeight > div:first-child': { maxHeight: 450 },
    '& > div:not(first-child)': { zIndex: theme.zIndex.table.scrollbar },
  },
  tableContentsWrap: {
    display: 'flex',
    '& > div': { flex: '1 0 auto' },
    '&.useFetch': { maxHeight: '730px' },
  },
  tableSection: { borderCollapse: 'separate', '& P': { margin: 0 } },
  tableRangeBorder: (props) => {
    const { borderRangeStart, borderRangeEnd } = props;
    if (!borderRangeStart || !borderRangeEnd) return {};

    return {
      [`& table th:nth-child(${borderRangeStart}), & table td:nth-child(${borderRangeStart})`]: {
        borderLeft: '3px solid #999 !important',
      },
      [`& table th:nth-child(${borderRangeEnd}), & table td:nth-child(${borderRangeEnd})`]: {
        borderRight: '3px solid #999 !important',
      },
      [`& table thead th:nth-child(n+${borderRangeStart}):nth-child(-n+${borderRangeEnd})`]: {
        borderTop: '3px solid #999 !important',
      },
      [`& table tbody:last-of-type tr:last-child td:nth-child(n+${borderRangeStart}):nth-child(-n+${borderRangeEnd})`]: {
        borderBottom: '3px solid #999 !important',
      },
    };
  },
}));

let fullTableHeight;
const useReference = () => {
  const [getScrollRef] = useState(() => createRef());
  return getScrollRef;
};

let pathName = window.location.pathname;
const EnhancedTableContainer = React.memo((props) => {
  const classes = useStyles({
    borderRangeStart: props.borderRangeStart,
    borderRangeEnd: props.borderRangeEnd,
  });
  const { t } = useTranslation();
  const scrollRef = useReference();
  const dispatch = useDispatch();
  const hoverCount = useRef(0);

  const {
    params = {},
    loading: propsLoading = false,
    dimmingLoading = false,
    dataReqPromise = (f) => f,
    customized = {},
    addColumnList = new Map(),
    title = undefined,
    toolTip = undefined,
    dense = true,
    className = {},
    pointer = false,
    useFetch = true,
    usePagenation = true,
    rowsPerPageOptions = [
      5,
      10,
      25,
      50,
      100,
      200,
      500,
      { label: 'All', value: -1 },
    ],
    useTotal = true,
    useSearch = true,
    useColumSet = false,
    useCheckBox = false,
    useExcel = true,
    isIncludingTotal = false,
    noFetch = false,
    expandTable = false,
    excelTotalRemoval = false,
    excelItemsRemoval = null,
    excelTransKey = null,
    paramsRemoval = null,
    reFetch = false,
    getParam = (f) => f,
    callBack = (f) => f,
    dataCallBack = (f) => f,
    rowsPerPage: propsRowsPerPage = 10,
    dataDetailReqPromise,
    makeSubParam,
    useTotalSubList = false,
    tableTitle = undefined,
    childColumnSet = {},
    isModalHeight,
    location,
    hasActController = { edit: true }, // 테이블 checkBox 버튼 컨트롤러
    headCallBack = (f) => f, // 테이블 헤드에서 사용되는 callBack함수
    reFetchHead = false,
    paramsInHead = false,
    propsTotalList = [],
    propsList = [],
    propsHeadCells = new Map(),
    useCustomTable = false,
    useFullScreen = true,
    useReFetchBtn = false,
    useToolBar = true,
    useStoreLoading = true,
    useRowData = false,
    handleRowClick = (f) => f,
    rowColor = {},
  } = props;
  const { isFrame } = queryString.parse(location.search);
  // 내부적으로 sort를 사용하는 페이지는 limit를 0으로 던지면 백단에서 무제한으로 인식함.
  const [initList, setInitList] = useState(propsList);
  const [list, setList] = useState(propsList);
  const [initTotalList, setInitTotalList] = useState({});
  const [totalList, setTotalList] = useState({});
  const [totalStatus, setTotalStatus] = useState('loading');
  const [listStatus, setListStatus] = useState('loading');
  const [page, setPage] = useState(0);
  const [noRequests, setNoRequests] = useState(false);
  const initLoading = { more: false, excel: false, body: false };
  const [loading, setLoading] = useState(initLoading);
  const [headCells, setHeadCells] = useState(propsHeadCells);
  const [checkList, setCheckList] = useState([]);
  const [newSelected, setNewSelected] = useState([]);
  const [screenMode, setScreenMode] = useState('');
  const [sort, setSort] = useState(params.sort);
  const [order, setOrder] = useState(params.order);
  const [rowsPerPage, setRowsPerPage] = React.useState(propsRowsPerPage);
  const changeParams = useRef(false);
  const [useScrollEvent, setUseScrollEvent] = useState(true);
  const { loadingState } = useSelector((state) => state.filter);
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  };
  const prevParams = usePrevious(params);

  useEffect(() => {
    if (useStoreLoading) {
      const setTableLoading = (state) => {
        dispatch(tableChangeLoading(state));
      };
      if (
        loading.body === true ||
        loading.excel === true ||
        loading.more === true ||
        propsLoading === true ||
        totalStatus === true
      ) {
        if (loadingState.table === false) {
          setTableLoading(true);
        }
      } else {
        if (loadingState.table === true) {
          setTableLoading(false);
        }
      }
    }
    //eslint-disable-next-line
  }, [loading, propsLoading, totalStatus, loadingState]);

  //마운트 이후 디폴트 실행
  useEffect(() => {
    if (!reFetchHead && !useCustomTable) headFetch();
    if (useCustomTable) {
      setTotalStatus('');
      setListStatus('');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (useCustomTable) {
      if (JSON.stringify(propsList) !== JSON.stringify(initList)) {
        if (propsList.length === 0) setListStatus('noData');
        else setListStatus('');
        setList(propsList);
        setInitList(propsList);
      }
      if (JSON.stringify(propsTotalList) !== JSON.stringify(totalList)) {
        if (propsTotalList.length === 0) setTotalStatus('noData');
        else setTotalStatus('');
        setTotalList(propsTotalList);
        setInitTotalList(propsTotalList);
      }
      if (propsHeadCells !== headCells) setHeadCells(propsHeadCells);
    }
    // eslint-disable-next-line
  }, [propsList, propsTotalList, propsHeadCells]);

  useEffect(() => {
    if (reFetch) {
      setNoRequests(false);
      setNewSelected([]);
      setCheckList([]);
      setPage(0);
      if (!useCustomTable) {
        fetchList();
      }
    } // eslint-disable-next-line
  }, [reFetch]);

  useEffect(() => {
    if (
      prevParams !== undefined &&
      JSON.stringify(prevParams) !== JSON.stringify(params)
    ) {
      const isChangeSort = prevParams.sort !== params.sort;
      const isChangeOrder = prevParams.order !== params.order;
      const isChangeKeyword = prevParams.keyword !== params.keyword;
      // 초기 params 이후
      changeParams.current = true;
      if (isChangeSort && isChangeOrder) {
        setOrder(params.order);
        setSort(params.sort);
        setPage(0);
      } else if (isChangeOrder) {
        setOrder(params.order);
        setPage(0);
      } else if (isChangeSort) {
        setSort(params.sort);
        setPage(0);
      }
      if (typeof prevParams.sort === 'undefined') {
        setSort(params.sort);
      }
      if (typeof prevParams.order === 'undefined') {
        setOrder(params.order);
      }

      if (page !== 0) {
        setPage(0);
      }
      setNewSelected([]);
      setCheckList([]);
      setNoRequests(false);
      if (useFetch) {
        fetchList();
      } else {
        const tempPrevParams = { ...prevParams };
        const tempParams = { ...params };
        delete tempPrevParams['keyword'];
        delete tempParams['keyword'];
        const condition1 =
          !isChangeSort &&
          !isChangeOrder &&
          !isChangeKeyword &&
          noFetch === false;
        const condition2 =
          JSON.stringify(tempPrevParams) !== JSON.stringify(tempParams) &&
          !isChangeSort &&
          !isChangeOrder;
        if (condition1 || condition2) {
          fetchList();
        }
      }
    } else if (prevParams === undefined) {
      // 완전 초기 params
      if (!useCustomTable) {
        fetchList();
      }
    }
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (useFetch && !useCustomTable) {
      if (page > 0) {
        if (!useCustomTable) {
          fetchList();
        }
      }
    } else {
      setNewSelected([]);
      setCheckList([]);
    }
    if (changeParams.current) {
      if (
        useFetch &&
        !useCustomTable &&
        JSON.stringify(prevParams) !== JSON.stringify(params)
      ) {
        fetchList();
      }
    }
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const conversionUri = (paramUrl) => {
    let resultUri;
    pathName = window.location.pathname;
    if (
      paramUrl !== '' &&
      typeof paramUrl === 'string' &&
      typeof paramUrl !== 'undefined' &&
      typeof pathName !== 'undefined'
    ) {
      resultUri = paramUrl;
    } else {
      resultUri = pathName;
    }
    return resultUri;
  };

  const headFetch = () => {
    const { uri } = params;
    pathName = window.location.pathname;
    let headParams = {
      table: typeof params.table !== 'undefined' ? params.table : null,
      uri: typeof uri !== 'undefined' ? conversionUri(uri) : pathName,
    };
    if (paramsInHead) {
      headParams = { ...headParams, ...params };
    }
    fetchHeader(tableHeaders, addColumnList, headParams).then((headers) =>
      setHeadCells(headers)
    );
  };

  /**
   * 리스트를 받아옴
   */
  const fetchList = () => {
    const { uri, keyword } = params;
    if (useCustomTable === false) {
      if (page === 0) {
        setTotalStatus('loading');
      }
      if (reFetchHead) {
        headFetch();
      }
      let newParams = {
        ...params,
        sort: params.sort,
        order: under2camel(params.order),
        uri: conversionUri(uri),
      };
      if (!!paramsRemoval) {
        for (let removalKey of paramsRemoval) {
          delete newParams[removalKey];
        }
      }
      if (typeof keyword === 'undefined' || keyword === '') {
        delete newParams['keyword'];
      } else {
        newParams = { ...newParams, keyword: nullConversion(keyword) };
      }
      if (useTotal && isIncludingTotal === false) {
        if (noRequests ? true : page === 0) {
          setInitTotalList({});
          setTotalList({});
          let totalParams = { ...newParams, total: 1 };
          delete totalParams['limit'];
          if (useTotalSubList) {
            totalParams.sumSub = 1;
          }
          dataReqPromise({ params: { ...paramConverter(totalParams) } })
            .then((response) => {
              //추가데이터가 있다면 콜백함수 실행
              const { data } = response.data;
              if (data.length === 0) {
                setInitTotalList({});
                setTotalList({});
                setTotalStatus('noData');
                callBack('noData');
              } else {
                setTotalList(data[0]);
                setInitTotalList(data[0]);
                setTotalStatus('');
                callBack('success', data[0]);
              }
            })
            .catch((error) => {
              setTotalStatus('error');
              handleClickSnackbar({
                message: error,
                option: { type: 'error' },
              });
            });
        }
      }

      if (reFetch) {
        setList([]);
        setLoading({ ...loading, body: true });
      } else {
        if (page === 0) {
          setList([]);
          setLoading({ ...loading, body: true });
        } else {
          if (noRequests === false) {
            setLoading({ ...loading, more: true });
          } else {
            setList([]);
            setLoading({ ...loading, body: true });
          }
        }
      }
      if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
        newParams = { ...newParams, page: 1 };
        if (newParams['page'] === 1) {
          setListStatus('loading');
        }
      } else {
        newParams = { ...newParams, page: noRequests ? 1 : page + 1 };
      }
      // 통신 없이 sort를 사용할 경우 limit를 0(무제한)으로 데이터 요청
      if (useFetch === false) {
        newParams = { ...newParams, limit: 0 };
        delete newParams['page'];
        delete newParams['keyword'];
      } else {
        if (typeof newParams.limit === 'undefined') {
          newParams = { ...newParams, limit: 50 };
        }
      }
      dataReqPromise({ params: { ...paramConverter(newParams) } })
        .then((response) => {
          const { data } = response.data;
          if (data.length === 0) {
            if (newParams['page'] > 1) {
              // 스크롤 이벤트로 페이징을 넘어갈때 통신하게되었을때 더이상 데이터가 없으면 스낵바로 피드백을 줌.
              handleSetNoData();
              setPage(0);
            } else {
              if (typeof dataCallBack !== 'undefined') {
                dataCallBack('noData');
              }
              setListStatus('noData');
              if (isIncludingTotal) {
                setTotalStatus('noData');
              }
              callBack('noData');
            }
          } else {
            if (newParams['page'] > 1) {
              let newList = [];
              if (typeof data.subData !== 'undefined') {
                newList = list.concat(data.list);
                if (data.list.length === 0) {
                  handleSetNoData();
                }
              } else {
                if (data.length === 0) {
                  handleSetNoData();
                }
                newList = list.concat(data);
              }
              handleAddListData(newList, undefined);
              setListStatus('');
            } else {
              if (isIncludingTotal) {
                if (typeof data.subData !== 'undefined') {
                  let newTotalList = {};
                  let newList = [];
                  if (data.list.length === 0) {
                    setTotalStatus('noData');
                    setListStatus('noData');
                  } else {
                    newTotalList = data.list[0];
                    data.list.shift();
                    newList = data.list;
                    setTotalStatus('');
                    setListStatus('');
                  }
                  handleAddListData(newList, newTotalList);
                  if (typeof dataCallBack !== 'undefined') {
                    dataCallBack(data.subData);
                  }
                } else {
                  const newTotalList = data[0];
                  data.shift();
                  handleAddListData(data, newTotalList);
                  setTotalStatus('');
                  setListStatus('');
                }
              } else {
                if (typeof data.subData !== 'undefined') {
                  if (typeof dataCallBack !== 'undefined') {
                    dataCallBack(data.subData);
                  }
                  handleAddListData(data.list);
                } else {
                  handleAddListData(data);
                }
                setListStatus('');
              }
            }
            callBack('success', data);
          }
        })
        .catch((error) => {
          setListStatus('error');
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        })
        .finally(() => {
          setLoading({ ...loading, more: false, body: false });
          changeParams.current = false;
          setUseScrollEvent(true);
        });
    }
  };

  const handleSetNoData = () => {
    setNoRequests(true);
    handleClickSnackbar({
      message: t('추가 데이터가 없습니다.'),
      option: { type: 'info' },
    });
  };

  const handleAddListData = (newList, newTotal) => {
    if (typeof newTotal !== 'undefined') {
      setInitTotalList(newTotal);
      setTotalList(newTotal);
    }
    if (typeof newList !== 'undefined') {
      setList(newList);
      setInitList(newList);
    }
  };

  const handleAddSubList = (row) => {
    if (typeof dataDetailReqPromise !== 'undefined') {
      let reqParams = { ...params };
      delete reqParams['sort'];
      delete reqParams['order'];
      setLoading({ ...loading, more: true });
      dataDetailReqPromise({
        params: {
          ...paramConverter(reqParams),
          ...paramConverter(makeSubParam(row)),
        },
      })
        .then((response) => {
          const { data } = response.data;
          if (data.length === 0) {
            handleClickSnackbar({
              message: t('데이터가 없습니다.'),
              option: { type: 'info' },
            });
          } else {
            let newList = [...list];
            const iTemIndex = list.indexOf(row);
            newList[iTemIndex].SUB = [...data];
            setList(newList);
          }
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        })
        .finally(() => {
          setLoading({ ...loading, more: false });
        });
    } else {
      handleClickSnackbar({
        message: t('서브 데이터가 없습니다.'),
        option: { type: 'error' },
      });
    }
  };

  /**
   * 스크롤이벤트 (페이징처리시 사용)
   * @param event
   */
  const handleTableScroll = (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollTop !== 0 && scrollHeight - 1 <= scrollTop + clientHeight) {
      if (!noRequests) {
        setPage(page + 1);
        setUseScrollEvent(false);
      }
    }
  };

  /**
   * 엑셀 다운로드
   */
  const handleExcelDonload = () => {
    let newParams = {
      params: {
        ...paramConverter(params),
        excel: 1,
        order: under2camel(params.order),
      },
      responseType: 'blob',
    };
    delete newParams.params['limit'];
    if (excelTotalRemoval) {
      newParams = {
        ...newParams,
        params: { ...newParams.params, noneTotal: 1 },
      };
    }
    if (excelItemsRemoval !== null) {
      newParams = {
        ...newParams,
        params: { ...newParams.params, pass: excelItemsRemoval },
      };
    }
    if (excelTransKey !== null) {
      newParams = {
        ...newParams,
        params: { ...newParams.params, excelTransKey: excelTransKey },
      };
    }

    setLoading({ ...loading, excel: true });

    delete newParams['sumSub'];

    dataReqPromise(newParams)
      .then((response) => {
        fileDownload(response);
      })
      .catch((error) => {
        handleClickSnackbar({ message: error, option: { type: 'error' } });
      })
      .finally(() => {
        setLoading({ ...loading, excel: false });
      });
  };

  /**
   * 화면 모드 변경
   */
  const handleScreenMode = () => {
    fullTableHeight = window.innerHeight - 100;
    if (screenMode === '') {
      setScreenMode('full');
    } else {
      setScreenMode('');
    }
  };

  /**
   * 테이블 데이터를 새로고침하지 않고 sort를 변경하는 함수
   * @param array
   * @param comparator
   * @returns {*}
   */
  function desc(a, b, order) {
    let aData = a[order];
    let bData = b[order];

    if (!isNaN(aData) && !isNaN(bData)) {
      // 둘다 숫자면
      aData = Number(aData);
      bData = Number(bData);
    }
    if (bData < aData) {
      return -1;
    }
    if (bData > aData) {
      return 1;
    }
    return 0;
  }

  function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    function findHead(element) {
      if (element.id === order) {
        return true;
      }
    }
    const headData = headCells.find(findHead);
    stabilizedThis.sort((a, b) => {
      let newA = { ...a[0] };
      let newB = { ...b[0] };
      if (typeof headData !== 'undefined') {
        if (
          (!!newA[order] || newA[order] === 0) &&
          headData['calFlag'] !== false
        ) {
          newA[order] = converCalFlagData(order, headCells, newA);
        }
        if (
          (!!newB[order] || newB[order] === 0) &&
          headData['calFlag'] !== false
        ) {
          newB[order] = converCalFlagData(order, headCells, newB);
        }
      }
      const sort = cmp(newA, newB);
      if (sort !== 0) return sort;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getSorting(sort, order) {
    return sort === 'desc'
      ? (a, b) => desc(a, b, order)
      : (a, b) => -desc(a, b, order);
  }

  /**
   * 한번에 보는 데이터 갯수를 변경할때 사용하는 함수
   * @param event
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * 페이징 넘기는 함수
   * @param event
   * @param newPage
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * odering
   * @param {} event
   * @param {string} orderKey ordering 할 header id
   */
  const handleRequestSort = (event, orderKey) => {
    let isAsc;
    let newSort = '';
    let changeType = '';
    let newParams = { ...params };
    if (useFetch && !useCustomTable) {
      isAsc =
        params.order !== orderKey ||
        (params.order === orderKey && params.sort === 'asc');
      newSort = isAsc ? 'desc' : 'asc';
    } else {
      isAsc = order !== orderKey || (order === orderKey && sort === 'asc');
      setOrder(orderKey);
      newSort = isAsc ? 'desc' : 'asc';
      setSort(newSort);
    }
    setPage(0);
    setNewSelected([]);
    setCheckList([]);
    newParams = { ...newParams, sort: newSort, order: orderKey };
    if (params['order'] !== newParams['order']) {
      changeType = 'order';
    } else if (params['sort'] !== newSort) {
      changeType = 'sort';
    }
    getParam(newParams, changeType);
  };

  /**
   * 컬럼 설정 저장
   */
  const handleSaveColumnSet = (customColumn) => {
    const heads = [];
    for (let column of customColumn.values()) {
      const { id, displayFlag } = column;
      if (id === 'SEQ') {
        continue;
      }
      if (displayFlag) {
        heads.push(id);
      }
    }
    setCustom({ values: heads.join(','), type: 'table.head' })
      .then((response) => {})
      .catch((error) => {
        handleClickSnackbar({ message: error, option: { type: 'error' } });
      });
    setHeadCells(customColumn);
  };

  const handleSearch = (keyword) => {
    let newList = [];
    if (typeof keyword === 'undefined') {
      setTotalList(initTotalList);
      setList(initList);
      setListStatus('');
    } else {
      if (useFetch && !useCustomTable) {
        getParam({ ...params, keyword: keyword }, 'keyword');
      } else {
        for (let i = 0; i < initList.length; i++) {
          const listArray = Object.values(initList[i]);
          for (let listI = 0; listI < listArray.length; listI++) {
            if (String(listArray[listI]).indexOf(keyword) > -1) {
              newList.push(initList[i]);
              break;
            }
          }
        }
        if (!newList.length) {
          handleClickSnackbar({
            message: t('검색 결과가 없습니다.'),
            option: { type: 'error' },
          });
          setListStatus('noData');
          setTotalList(initTotalList);
        } else {
          setListStatus('');
        }
        setList(newList);
      }
    }
    setPage(0);
    setCheckList([]);
    setNewSelected([]);
  };

  /**
   * 좌우 스크롤안내 open
   */
  const handleInTableInfo = () => {
    const contentWidth =
      document.getElementById('tableBodyListWrap').offsetWidth;
    const tableWidth = document.getElementById('tableContents').offsetWidth;
    if (
      (propsLoading || loading.body) === false &&
      hoverCount.current < 1 &&
      contentWidth > tableWidth
    ) {
      hoverCount.current++;
      handleClickSnackbar({
        message: '좌우 스크롤 기능 안내 : Shift + 마우스 휠',
        option: {
          type: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        },
      });
    }
  };

  const handleClick = (e) => {
    let key = Number(e.currentTarget.dataset.key);
    let newSelectIndex = [...newSelected];
    let newCheckList = [...checkList];
    if (newSelectIndex.includes(key)) {
      // 체크리스트 index에서 다시한번 체크했을때 체크 리스트에서 삭제
      for (let i = 0; i < newSelectIndex.length; i++) {
        if (newSelectIndex[i] === key) {
          newSelectIndex.splice(i, 1);
        }
      }
      newCheckList = [];
      for (let checkIndex of newSelectIndex) {
        newCheckList.push(list[checkIndex]);
      }
    } else {
      // 현재 체크 리스트에서 없는 데이터가 왔을 경우
      newSelectIndex.push(key);
      if (useFetch && !useCustomTable) {
        newCheckList.push(list[key]);
      } else {
        newCheckList.push(stableSort(list, getSorting(sort, order))[key]);
      }
    }
    setNewSelected(newSelectIndex);
    setCheckList(newCheckList);
    callBack('checkbox', newCheckList);
  };

  const handleRowData = (e) => {
    let key = Number(e.currentTarget.dataset.key);
    let newList = stableSort(list, getSorting(sort, order));
    if (useRowData) {
      let clickRowData;
      if (page === 0) {
        clickRowData = newList[key];
      } else {
        clickRowData = newList.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )[key];
      }
      handleRowClick(clickRowData);
    }
  };

  const handleHeadClick = (checkState) => {
    let newList = [
      ...stableSort(list, getSorting(sort, order)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    ];

    if (checkState) {
      let newCheckIndex = [];
      if (useFetch) {
        for (let i = 0; i < list.length; i++) {
          newCheckIndex.push(i);
        }
        newList = list;
      } else {
        for (let i = 0; i < newList.length; i++) {
          newCheckIndex.push(i);
        }
      }
      setNewSelected(newCheckIndex);
      setCheckList(newList);
    } else {
      setNewSelected([]);
      setCheckList([]);
    }
    callBack('checkbox', checkState ? newList : []);
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <div
        className={clsx(
          screenMode === 'full' ? classes.fullscreen : '',
          className
        )}
      >
        <EnhancedTableWrap
          toolTip={toolTip}
          className={classes.tableWrap}
          columnSetButton={
            useColumSet ? (
              <DivisionWrap>
                <SetUpList
                  list={headCells}
                  customized={customized}
                  handleSaveCustomSet={handleSaveColumnSet}
                  title={t('컬럼설정')}
                  popoverTitle={t('나만의 기본 컬럼을 설정해보세요.')}
                  popoverNotice={t('설정 이후 선택한 컬럼만 노출됩니다.')}
                  saveButtonTitle={t('내 컬럼으로 설정')}
                />
              </DivisionWrap>
            ) : undefined
          }
          selectedCnt={newSelected.length}
          title={title}
          useExcel={useExcel}
          useSearch={useSearch}
          handleExcelDonload={handleExcelDonload}
          handleScreenMode={handleScreenMode}
          screenMode={screenMode}
          headcells={headCells}
          handleSearch={handleSearch}
          isFrame={isFrame}
          hasActController={hasActController}
          headCallBack={headCallBack}
          loadingExcel={loading.excel}
          checkList={checkList}
          loading={propsLoading || loading.body}
          params={params}
          useFullScreen={useFullScreen}
          useReFetchBtn={useReFetchBtn}
          handleFetch={fetchList}
          useToolBar={useToolBar}
          getParam={getParam}
        >
          {(dimmingLoading || loading.more) && (
            <div className={classes.dimming}>
              <CurrentStatus type="moreLoading" />
            </div>
          )}
          <Scrollbars
            className={clsx({ isModalHeight }, classes.tableCustomScrollBar)}
            onScroll={
              useFetch
                ? useScrollEvent
                  ? handleTableScroll
                  : (f) => f
                : undefined
            }
            ref={scrollRef}
            autoHide
          >
            <div
              id="tableContents"
              className={clsx(
                classes.tableContentsWrap,
                useFetch === true && 'useFetch',
                {
                  [classes.tableRangeBorder]: props.borderRangeStart && props.borderRangeEnd,
                }
              )}
              style={{ maxHeight: screenMode === 'full' && fullTableHeight }}
            >
              <div>
                {typeof tableTitle !== 'undefined' && <h6>{tableTitle}</h6>}
                <Table
                  aria-labelledby="tableTitle"
                  size={dense ? 'small' : 'medium'}
                  classes={{
                    root: classes.tableSection,
                  }}
                >
                  <EnhancedTableHead
                    loading={
                      propsLoading ||
                      loading.body ||
                      loading.excel ||
                      loading.more ||
                      propsLoading
                    }
                    order={useFetch && !useCustomTable ? params.order : order}
                    sort={useFetch && !useCustomTable ? params.sort : sort}
                    onRequestSort={handleRequestSort}
                    allCheck={newSelected.length === list.length}
                    newSelected={newSelected}
                    list={
                      useFetch
                        ? list
                        : stableSort(list, getSorting(sort, order)).slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                    }
                    headCells={headCells}
                    useCheckBox={useCheckBox}
                    customized={customized}
                    expandTable={expandTable}
                    handleHeadClick={handleHeadClick}
                    useFetch={useFetch}
                  />
                  <EnhancedTableBody
                    loading={propsLoading || loading.body}
                    headCells={headCells}
                    list={stableSort(list, getSorting(sort, order))}
                    totalList={totalList}
                    tableState={{
                      total: totalStatus,
                      list: listStatus,
                    }}
                    useCheckBox={useCheckBox}
                    useTotal={useTotal}
                    pointer={pointer}
                    page={page}
                    customized={customized}
                    expandTable={expandTable}
                    childColumnSet={childColumnSet}
                    onMouseEnter={handleInTableInfo}
                    handleAddSubList={handleAddSubList}
                    dataDetailReqPromise={dataDetailReqPromise}
                    handleClick={handleClick}
                    screenMode={screenMode}
                    newSelected={newSelected}
                    rowsPerPage={rowsPerPage}
                    useFetch={useFetch}
                    useRowData={useRowData}
                    handleRowData={handleRowData}
                    useCustomTable={useCustomTable}
                    handleClickSnackbar={handleClickSnackbar}
                    rowColor={rowColor}
                  />
                </Table>
              </div>
            </div>
          </Scrollbars>
          {propsLoading || loading.body
            ? null
            : useFetch === false &&
              usePagenation && (
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  component="div"
                  count={list.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
        </EnhancedTableWrap>
      </div>
    </>
  );
});

EnhancedTableContainer.propTypes = {
  params: PropTypes.object.isRequired,
  dataReqPromise: PropTypes.func,
  getParam: PropTypes.func.isRequired,
  callBack: PropTypes.func,
  loading: PropTypes.bool,
};

const isEqual = (prevProps, nextProps) => {
  let prev = { ...prevProps };
  let next = { ...nextProps };
  if (
    typeof prev['toolTip'] !== 'string' &&
    typeof next['toolTip'] !== 'string'
  ) {
    delete prev['toolTip'];
    delete next['toolTip'];
  }
  if (typeof prev['title'] !== 'string' && typeof next['title'] !== 'string') {
    delete prev['title'];
    delete next['title'];
  }
  let equal = JSON.stringify(prev) !== JSON.stringify(next);
  return !equal;
};

export default React.memo(withRouter(EnhancedTableContainer), isEqual);
